import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowClockwise, Tray, CheckCircle, EyeSlash, Lightning, Gear, Printer, UploadSimple, Paperclip, ArrowBendUpLeft } from "@phosphor-icons/react";
import ScanInboxSettings from "@/components/communication/ScanInboxSettings";
import { ScanAiBox, ScanPreview } from "@/components/communication/ScanParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SearchableCombobox from "@/components/SearchableCombobox";
import MessagingDialog from "@/components/MessagingDialog";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDateTime } from "@/components/communication/commMeta";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";

const FILE_CATS = [["Verträge", "Verträge"], ["Vertragsänderung", "Vertragsänderung / Nachtrag"], ["Amtliche Dokumente", "Amtliche Dokumente"],
  ["Übergabeprotokoll", "Protokolle"], ["Betriebskosten", "Betriebskosten"], ["Rechnungen", "Rechnungen"], ["Versicherung", "Versicherung"],
  ["Mieterhöhung", "Mieterhöhung"], ["Fotos", "Fotos"], ["Sonstiges", "Sonstige Dokumente"]];

const TABS = [
  { k: "suggested,new", l: "Zuzuordnen" }, { k: "assigned", l: "Zugeordnet" }, { k: "ignored", l: "Ignoriert" },
];
const arr = (d) => (Array.isArray(d) ? d : d?.items || []);
const boxShort = (b) => (b || "").slice(7).split("@")[0] + "@";
const inBox = (m, src) => src === "all" || (src === "scan" ? m.kind === "scan" : src === "me" ? (m.mailbox || "").startsWith("me:") : m.mailbox === src);
const cname = (c) => c.display_name || c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();

function useOptions() {
  const [o, setO] = useState({ tenancies: [], properties: [], contacts: [], vorgaenge: [] });
  useEffect(() => {
    Promise.all([api.get("/tenancies", { params: { limit: 1000 } }), api.get("/properties"), api.get("/contacts", { params: { limit: 1000 } }), api.get("/vorgaenge")])
      .then(([t, p, c, v]) => {
        const tn = (x) => [].concat(x.tenant_names || x.tenant_name || []).join(", ");
        const byT = Object.fromEntries(arr(t.data).map((x) => [x.id, `${x.contract_number || ""} ${tn(x)}`.trim()]));
        setO({
        tenancies: arr(t.data).map((x) => ({ id: x.id, label: `${x.contract_number || ""} · ${tn(x)} · ${x.property_name || ""}`.replace(/( · )+$/, ""), keywords: tn(x) })),
        properties: arr(p.data).map((x) => ({ id: x.id, label: x.name })),
        contacts: arr(c.data).map((x) => ({ id: x.id, label: cname(x), keywords: (x.emails || []).map((e) => e.address || e).join(" ") })),
        vorgaenge: arr(v.data).map((x) => ({ id: x.id, label: `${x.case_number} · ${x.title}${x.tenancy_id && byT[x.tenancy_id] ? ` · ${byT[x.tenancy_id]}` : ""}`,
          keywords: [byT[x.tenancy_id], ...(x.participants || []).map((pp) => pp.name || pp.display_name || "")].filter(Boolean).join(" ") })),
        });
      }).catch(() => {});
  }, []);
  return o;
}

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function MailAttachments({ mid, atts, pending }) {
  const open = async (a) => {
    const w = window.open("", "_blank");
    try {
      const res = await api.get(`/mailbox/messages/${mid}/attachments/${a.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: a.content_type }));
      const viewable = /^(application\/pdf|image\/|text\/)/.test(a.content_type);
      if (viewable && w) { w.location.href = url; return; }
      w?.close();
      const link = document.createElement("a"); link.href = url; link.download = a.name; link.click();
    } catch (e) {
      w?.close();
      const d = e?.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).detail : e?.response?.data?.detail;
      toast.error(d || "Anhang konnte nicht geladen werden");
    }
  };
  if (!atts.length) return pending ? <div className="text-xs text-slate-500" data-testid="mail-attachments-loading">Anhänge werden geladen…</div> : null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="mail-attachments">
      {atts.map((a) => (
        <button key={a.id} type="button" onClick={() => open(a)} title="Öffnen"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs hover:bg-slate-50 hover:border-slate-300 transition-colors" data-testid={`mail-attachment-${a.id}`}>
          <Paperclip size={13} className="text-slate-500" /><span className="max-w-[220px] truncate font-medium">{a.name}</span><span className="text-slate-400">{fmtSize(a.size)}</span>
        </button>
      ))}
    </div>
  );
}

function Detail({ m: m0, opts, onDone }) {
  const [m, setM] = useState(m0);
  useEffect(() => { setM(m0); }, [m0]);
  const isScan = m.kind === "scan";
  const [html, setHtml] = useState("");
  const [atts, setAtts] = useState([]);
  const [t, setT] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setHtml(""); setAtts([]); const s = m.suggestion || {};
    setT({ contact_id: s.contact_id || "", tenancy_id: s.tenancy_id || "", property_id: s.property_id || "", vorgang_id: s.vorgang_id || "" });
    if (m.kind !== "scan") api.get(`/mailbox/messages/${m.id}/body`).then(({ data }) => { setHtml(data.html); setAtts(data.attachments || []); }).catch((e) => setHtml(`<p style="color:#b91c1c">${e?.response?.data?.detail || "Inhalt nicht ladbar"}</p>`));
    // nur beim Wechsel der Nachricht zurücksetzen – nicht bei jedem Listen-Refresh (sonst „fliegt“ die Auswahl raus)
  }, [m.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const assign = async () => {
    setBusy(true);
    try { await api.post(`/mailbox/messages/${m.id}/assign`, Object.fromEntries(Object.entries(t).filter(([, v]) => v))); toast.success(isScan ? "Scan zugeordnet und in der Kommunikation abgelegt" : "E-Mail zugeordnet und in der Kommunikation abgelegt"); window.dispatchEvent(new Event("domivus:postfach-changed")); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Zuordnung fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const ignore = async () => { await api.post(`/mailbox/messages/${m.id}/ignore`); onDone(); };
  const [fileCat, setFileCat] = useState("__comm");
  const [docMeta, setDocMeta] = useState({ title: "", date: "" });
  useEffect(() => { setFileCat("__comm"); setDocMeta({ title: m.ai?.title || m.subject || "", date: (m.ai?.document_date || m.received_at || "").slice(0, 10) }); }, [m.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const asDoc = fileCat !== "__comm";
  const fileDoc = async () => {
    setBusy(true);
    try {
      const body = { category: fileCat, title: docMeta.title || undefined, document_date: docMeta.date || undefined, ...Object.fromEntries(Object.entries(t).filter(([, v]) => v)) };
      const { data } = await api.post(`/mailbox/messages/${m.id}/file-document`, body);
      toast.success(`${data.document_ids.length} Dokument(e) unter „${FILE_CATS.find(([v]) => v === fileCat)?.[1]}“ abgelegt`); window.dispatchEvent(new Event("domivus:postfach-changed")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ablage fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const [reply, setReply] = useState(false);
  const esc = (x) => String(x || "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const quote = () => {
    const src = (html || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|tr|li|h[1-6]|blockquote)>/gi, "\n");
    const txt = (new DOMParser().parseFromString(src, "text/html").body?.textContent || m.preview || "").replace(/\n{3,}/g, "\n\n").trim().slice(0, 4000);
    return `<p></p><p></p><p>Am ${esc(fmtDateTime(m.received_at))} schrieb ${esc(m.from_name || m.from_email)}:</p><blockquote>${txt.split(/\n+/).map((l) => `<p>${esc(l)}</p>`).join("")}</blockquote>`;
  };
  const pick = (c) => setT((x) => ({ ...x, [`${c.type}_id`]: c.id }));
  const field = (k, label, options) => (
    <div><Label className="text-xs">{label}</Label>
      <SearchableCombobox value={t[k] || ""} onChange={(v) => setT((x) => ({ ...x, [k]: v }))} options={options} placeholder="—" testId={`mail-assign-${k}`} /></div>
  );
  return (
    <div className="space-y-3" data-testid="mail-detail">
      <div>
        <div className="text-base font-semibold text-slate-900" data-testid="mail-detail-subject">{m.subject || "(ohne Betreff)"}</div>
        <div className="text-xs text-slate-500">{isScan && <span className="inline-flex items-center gap-1 mr-1 text-indigo-700"><Printer size={12} /> Gescanntes Dokument ({m.file?.name}) ·</span>}{m.from_name}{m.from_email ? ` <${m.from_email}>` : ""} · {fmtDateTime(m.received_at)}{m.mailbox?.startsWith("shared:") ? ` · Freigegebenes Postfach ${m.mailbox.slice(7)}` : ""}</div>
      </div>
      {!isScan && m.from_email && <Button size="sm" variant="outline" className="gap-1" onClick={() => setReply(true)} data-testid="mail-reply-btn"><ArrowBendUpLeft size={14} /> Im Editor beantworten</Button>}
      {reply && <MessagingDialog open={reply} onOpenChange={setReply} defaultEmail={m.from_email}
        defaultSubject={/^(AW|RE|Antw)\s*:/i.test(m.subject || "") ? m.subject : `AW: ${m.subject || ""}`} initialBody={quote()}
        context={{ contact_id: t.contact_id || undefined, tenancy_id: t.tenancy_id || undefined, vorgang_id: t.vorgang_id || undefined, property_id: t.property_id || undefined }} />}
      {isScan && <ScanAiBox m={m} onUpdated={setM} />}
      {m.status !== "assigned" && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
          {m.suggestion?.reason && <div className="flex items-center gap-1 text-xs text-slate-700"><Lightning size={12} className="text-amber-500" /> Vorschlag: {m.suggestion.reason}</div>}
          {m.suggestion?.candidates?.length > 0 && (
            <div className="flex flex-wrap gap-1">{m.suggestion.candidates.map((c) => (
              <button key={c.id} type="button" onClick={() => pick(c)} className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 bg-white hover:bg-slate-100" data-testid={`mail-candidate-${c.id}`}>{c.label}</button>))}</div>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="sm:col-span-2"><Label className="text-xs">Ablegen als</Label>
              <Select value={fileCat} onValueChange={setFileCat}>
                <SelectTrigger data-testid="mail-file-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__comm">{isScan ? "Eingangspost (Kommunikation, mit Absender)" : "E-Mail-Korrespondenz (Kommunikation, mit Absender)"}</SelectItem>
                  {FILE_CATS.map(([v, l]) => <SelectItem key={v} value={v}>Dokument: {l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {asDoc && <>
              <div><Label className="text-xs">Titel</Label><Input value={docMeta.title} onChange={(e) => setDocMeta((s) => ({ ...s, title: e.target.value }))} data-testid="mail-file-title" /></div>
              <div><Label className="text-xs">Dokumentdatum</Label><DatePicker value={docMeta.date} onChange={(v) => setDocMeta((s) => ({ ...s, date: v }))} data-testid="mail-file-date" /></div>
            </>}
            {field("tenancy_id", "Mietverhältnis", opts.tenancies)}
            {field("property_id", "Objekt", opts.properties)}
            {field("contact_id", asDoc ? "Kontakt (betrifft, optional)" : "Kontakt (Absender)", opts.contacts)}
            {field("vorgang_id", "Vorgang / Aktenzeichen", opts.vorgaenge)}
          </div>
          {asDoc && <div className="text-[11px] text-slate-500" data-testid="mail-file-hint">Wird als {isScan ? "Scan" : "Anhang"} direkt unter „{FILE_CATS.find(([v]) => v === fileCat)?.[1]}“ abgelegt – ohne Absender und ohne Eintrag in der Kommunikation.</div>}
          <div className="flex gap-2">
            {asDoc
              ? <Button size="sm" disabled={busy || !Object.values(t).some(Boolean)} onClick={fileDoc} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="mail-file-btn"><CheckCircle size={14} /> {busy ? "Lege ab…" : "Als Dokument ablegen"}</Button>
              : <Button size="sm" disabled={busy || !Object.values(t).some(Boolean)} onClick={assign} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="mail-assign-btn"><CheckCircle size={14} /> {busy ? "Übernehme…" : "Zuordnen & ablegen"}</Button>}
            <Button size="sm" variant="outline" className="gap-1" onClick={ignore} data-testid="mail-ignore-btn"><EyeSlash size={14} /> Ignorieren</Button>
          </div>
        </div>
      )}
      {m.status === "assigned" && <div className="text-xs text-emerald-700" data-testid="mail-assigned-hint">Zugeordnet{m.assigned_auto ? " (automatisch)" : ""} am {fmtDateTime(m.assigned_at)} – {m.filed_category ? `als Dokument unter „${m.filed_category}“ abgelegt.` : "liegt in der Kommunikation."}</div>}
      {!isScan && <MailAttachments mid={m.id} atts={atts} pending={m.has_attachments && !html} />}
      {isScan ? <ScanPreview m={m} /> : <iframe title="mail" sandbox="" srcDoc={html} className="w-full h-[420px] border border-slate-200 rounded-md bg-white" data-testid="mail-body" />}
    </div>
  );
}

export default function Postfach() {
  const { user } = useAuth();
  const [st, setSt] = useState(null);
  const [tab, setTab] = useState(TABS[0].k);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], counts: {} });
  const [sel, setSel] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [shared, setShared] = useState("");
  const [showCfg, setShowCfg] = useState(false);
  const [src, setSrc] = useState("all");
  const [uploading, setUploading] = useState(false);
  const opts = useOptions();
  const load = useCallback(() => api.get("/mailbox/messages", { params: { status: tab, q: q || undefined } }).then(({ data: d }) => setData(d)), [tab, q]);
  const items = data.items.filter((m) => inBox(m, src));
  const sharedBoxes = (data.mailboxes || []).filter((b) => b.startsWith("shared:"));
  const boxErrors = (st?.boxes || []).filter((b) => b.last_error);
  const pending = data.items.some((m) => m.kind === "scan" && ["pending", "running"].includes(m.ai?.status));
  useEffect(() => { if (!pending) return undefined; const t = setTimeout(load, 4000); return () => clearTimeout(t); }, [pending, data, load]);
  const upload = async (e) => {
    const files = [...(e.target.files || [])]; e.target.value = "";
    if (!files.length) return;
    const fd = new FormData(); files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try { const { data: r } = await api.post("/mailbox/scan-upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`${r.ids.length} Scan(s) im Posteingang – KI liest das Dokument…`); setSrc("scan"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };
  const loadStatus = () => api.get("/mailbox/status").then(({ data: d }) => { setSt(d); setShared(d.shared_mailbox || ""); });
  const sync = useCallback(async (silent) => {
    setSyncing(true);
    try { const { data: r } = await api.post("/mailbox/sync"); if (!silent) toast.success(`${r.new} neue E-Mail${r.new === 1 ? "" : "s"} · ${r.auto_assigned} automatisch zugeordnet`); r.errors?.forEach((e) => toast.warning(e)); load(); loadStatus(); }
    catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "Abruf fehlgeschlagen"); }
    finally { setSyncing(false); }
  }, [load]);
  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { const iv = setInterval(() => { load(); loadStatus(); }, 30 * 1000); return () => clearInterval(iv); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (!st?.can_read) return undefined;
    sync(true);
    const iv = setInterval(() => sync(true), 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [st?.can_read]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveShared = async () => {
    try { await api.put("/mailbox/settings", { shared_mailbox: shared }); toast.success("Freigegebenes Postfach gespeichert – wird jetzt abgerufen"); await loadStatus(); setShowCfg(false); if (st?.can_read) sync(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  return (
    <div className="space-y-4" data-testid="postfach-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Tray size={24} /> Postfach</h1>
          <p className="text-xs text-slate-500">Outlook-Mails und gescannte Post an einem Ort. Mails bleiben unverändert in Outlook; Scans kommen per E-Mail vom Scanner und werden per KI gelesen und zur Zuordnung vorgeschlagen.</p>
        </div>
        {st?.last_sync && <span className="text-[11px] text-slate-500">Zuletzt abgerufen {fmtDateTime(st.last_sync)}</span>}
        <label className={`inline-flex items-center gap-1 h-8 px-3 rounded-md border border-slate-300 text-sm cursor-pointer hover:bg-slate-50 ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="scan-upload-btn">
          <UploadSimple size={14} /> {uploading ? "Lade hoch…" : "Scan hochladen"}
          <input type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={upload} data-testid="scan-upload-input" />
        </label>
        <Button size="sm" variant="ghost" onClick={() => setShowCfg((x) => !x)} data-testid="postfach-settings-btn"><Gear size={14} /></Button>
        <Button size="sm" onClick={() => sync(false)} disabled={syncing || !st?.can_read} className="gap-1 bg-slate-900 hover:bg-slate-800" data-testid="postfach-sync-btn"><ArrowClockwise size={14} className={syncing ? "animate-spin" : ""} /> Abrufen</Button>
      </div>
      {st && !st.can_read && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="postfach-connect-hint">
          {st.linked ? "Für das Postfach fehlt die Leseberechtigung. Bitte unter Profil › Outlook einmal trennen und neu verbinden."
            : "Kein Outlook-/Microsoft-365-Konto verbunden. Bitte unter Profil › Outlook verbinden."}
        </div>
      )}
      {boxErrors.map((b) => (
        <div key={b.mailbox} className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid={`postfach-box-error-${b.mailbox.split(":")[0]}`}>
          {b.mailbox.startsWith("shared:") ? `Freigegebenes Postfach ${b.mailbox.slice(7)}` : "Eigenes Postfach"} konnte nicht abgerufen werden ({fmtDateTime(b.last_error_at)}): {b.last_error}
        </div>
      ))}
      {showCfg && <ScanInboxSettings isAdmin={isAdmin} />}
      {showCfg && isAdmin && (
        <div className="flex items-end gap-2 rounded-md border border-slate-200 p-3" data-testid="postfach-settings">
          <div className="flex-1"><Label className="text-xs">Freigegebenes Postfach / Team-Postfach (z. B. info@ihre-firma.de) – Ihr Outlook-Konto braucht in Microsoft 365 Vollzugriff bzw. Leserechte darauf</Label>
            <Input value={shared} onChange={(e) => setShared(e.target.value)} placeholder="z. B. info@ihre-firma.de, vermietung@ihre-firma.de (mehrere mit Komma)" data-testid="postfach-shared-input" /></div>
          <Button size="sm" onClick={saveShared} data-testid="postfach-shared-save">Speichern</Button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => {
          const n = t.k.split(",").reduce((a, k) => a + (data.counts?.[k] || 0), 0);
          return <Button key={t.k} size="sm" variant={tab === t.k ? "default" : "outline"} onClick={() => { setTab(t.k); setSel(null); }} data-testid={`postfach-tab-${t.k.split(",")[0]}`}>{t.l} ({n})</Button>;
        })}
        <div className="ml-auto inline-flex rounded-md border border-slate-200 p-0.5" data-testid="postfach-source-filter">
          <span className="px-2 py-1 text-[11px] text-slate-400">Postfach</span>
          {[["all", "Alle"], ["me", "Mein Postfach"], ...sharedBoxes.map((b) => [b, b.slice(7)]), ["scan", "Scans"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setSrc(k); setSel(null); }} data-testid={`postfach-source-${k.startsWith("shared:") ? "shared" : k}`}
              className={`px-2.5 py-1 text-xs rounded ${src === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="w-56 h-9" data-testid="postfach-search" />
      </div>
      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        <div className="border border-slate-200 rounded-md bg-white divide-y divide-slate-100 max-h-[70vh] overflow-y-auto" data-testid="postfach-list">
          {items.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">Keine Einträge.</div>}
          {items.map((m) => (
            <button key={m.id} type="button" onClick={() => setSel(m)} data-testid={`mail-row-${m.id}`}
              className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${sel?.id === m.id ? "bg-slate-100" : ""}`}>
              <div className="flex items-center gap-2">{m.kind === "scan" && <Printer size={13} className="text-indigo-600 shrink-0" data-testid={`mail-row-scan-icon-${m.id}`} />}{(m.mailbox || "").startsWith("shared:") && <span className="text-[10px] px-1.5 rounded bg-teal-50 text-teal-800 border border-teal-200 shrink-0" data-testid={`mail-row-shared-tag-${m.id}`}>{boxShort(m.mailbox)}</span>}<span className="text-sm font-medium truncate">{m.kind === "scan" ? (m.ai?.doc_type_label || "Scan") : (m.from_name || m.from_email)}</span>
                {m.kind === "scan" && ["pending", "running"].includes(m.ai?.status) && <span className="text-[10px] text-indigo-600">liest…</span>}
                <span className="ml-auto text-[10px] text-slate-400 shrink-0">{fmtDateTime(m.received_at)}</span></div>
              <div className="text-xs text-slate-800 truncate">{m.subject}</div>
              <div className="text-[11px] text-slate-500 truncate">{m.status === "suggested" && <span className="text-amber-700">Vorschlag · </span>}{m.preview}</div>
            </button>
          ))}
        </div>
        <div className="border border-slate-200 rounded-md bg-white p-4 min-h-[300px]">
          {sel ? <Detail m={data.items.find((x) => x.id === sel.id) || sel} opts={opts} onDone={() => { setSel(null); load(); }} /> : <div className="text-sm text-slate-500">E-Mail oder Scan auswählen, um zuzuordnen.</div>}
        </div>
      </div>
    </div>
  );
}
